import { computed, h, defineComponent } from 'vue';
import { ItemProps } from '@/ui/components/inspectionReport/types';
import { useInspectionReport } from '@/ui/components/inspectionReport/useInspectioReport';
import { LegacyItem, LegacyNegativeAnswerItem } from '@/ui/components/inspectionReport/legacyTypes';
import { hasFeature } from '@/ui/auth/auth';
import {
  ConductionInfoTable,
  Issues,
  NegativeAnswers,
  Overview,
  ScoreTable,
} from '@/ui/components/inspectionReport/items';
import { t } from '@/ui/i18n/i18n';
import { InspectionItemType } from '@/entities/inspection/constants';
import { isRoccScoringEnabled, isEntityTypeOfResponseEnabled } from '@/ui/helpers/featureHelper';
import { findHeader, findResponseType } from '@/_legacy/store/checklist_builder';
import { ResponsesTypes } from '@/entities/checklist';
import { RoccScoreTable } from '@/ui/components/inspectionReport/items/RoccScoreTable';

export const HeaderPage = defineComponent<ItemProps>({
  name: 'HeaderPage',
  props: ['item'],
  setup(props) {
    const { resource } = useInspectionReport();

    const searchNegativeAnswers = (
      accumulator: Array<LegacyItem>,
      item: LegacyItem,
    ): Array<LegacyNegativeAnswerItem> => {
      if (item.type === InspectionItemType.Conditional && item.allConditionsMet !== true) {
        return accumulator;
      }
      if (item.type === InspectionItemType.Question && item.responded && item.negative) {
        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          max_score, score, photos, notes, issues,
          ...negativeAnswerItem
        } = item;
        return [...accumulator, negativeAnswerItem];
      }

      return [
        ...accumulator,
        ...(item.items ? item.items.reduce(searchNegativeAnswers, []) : []),
      ];
    };

    const formItems = computed(() => resource.value?.form?.items);
    const hasEntityItemQuestion = computed(() => {
      const headerPage = findHeader(resource.value?.form?.items);

      return isEntityTypeOfResponseEnabled() && findResponseType(headerPage, ResponsesTypes.entityItem);
    });

    const getEntityItemQuestions = () => {
      return resource.value?.template?.display_entity_item && resource.value.entity_item && !hasEntityItemQuestion.value
        ? [
          {
            title: t('inspection-report.entity-item'),
            type: 'question',
            response_type: 'entity_item_automatic',
            params: {
              automatic: true,
            },
          },
        ]
        : [];
    };

    return function render() {
      const items = [];

      const negativeAnswers = formItems.value
        ? formItems.value.reduce(searchNegativeAnswers, [])
        : [];

      if (hasFeature('scores')) {
        items.push(h(Overview, {
          props: {
            negativeAnswers,
          },
        }));
      }

      if (resource.value?.template?.display_score_result_of_pages) {
        items.push(h(ScoreTable));
      }

      if (resource.value?.template?.display_rocc_score && isRoccScoringEnabled()) {
        items.push(h(RoccScoreTable));
      }

      items.push(h(ConductionInfoTable, {
        props: {
          items: [
            ...getEntityItemQuestions(),
            ...(props.item.items ?? []),
          ],
        },
      }));

      if (resource.value?.template?.display_negative_answers) {
        items.push(
          h(NegativeAnswers, {
            props: {
              items: negativeAnswers,
            },
          }),
        );
      }

      if (resource.value?.template?.display_issues) {
        items.push(h(Issues));
      }

      return h('div', {
        class: ['inspection-report__header-page'],
        attrs: { 'data-test': 'header-page' },
      }, items);
    };
  },
});
